import React, { ReactNode } from 'react'

import { SxProps } from '@mui/material'

import Box from 'components_new/atoms/Box'
import Chip, { ChipProps } from 'components_new/atoms/Chip'
import Icon, { IconProps } from 'components_new/atoms/Icon'
import Text from 'components_new/atoms/Text'

import EditableTitle, {
  EditableTitleProps
} from 'components_new/molecules/EditableTitle'
import MetaRow, { MetaRowProps } from 'components_new/molecules/MetaRow'

interface HeroProps {
  actions?: ReactNode
  background?: boolean
  body?: ReactNode | null
  chip?: Omit<ChipProps, 'size'>
  disablePadding?: boolean
  icon?: IconProps
  metaItems?: MetaRowProps['items']
  editable?: {
    onSubmit: EditableTitleProps['onSubmit']
    placeholder: EditableTitleProps['placeholder']
    title: EditableTitleProps['value']
  }
  title?: ReactNode | null
  size?: 'large' | 'medium'
  sx?: SxProps
}

const Hero = React.forwardRef((props: HeroProps, ref: any) => {
  const {
    actions,
    background,
    body,
    chip,
    disablePadding,
    editable,
    icon,
    metaItems,
    title,
    size,
    sx
  } = props

  return (
    <Box
      ref={ref}
      sx={{
        borderRadius: 1,
        ...(disablePadding || !background
          ? null
          : { p: size === 'medium' ? 2 : 4, pt: size === 'medium' ? 3 : 6 }),
        ...(background
          ? {
              background:
                'linear-gradient(rgba(34, 197, 94, 0.12), transparent)'
            }
          : null),
        ...sx
      }}
    >
      <Box
        sx={{
          alignItems: !body ? 'center' : undefined,
          display: 'flex',
          gap: size === 'medium' ? 2 : 4
        }}
      >
        {icon ? (
          <Box
            sx={{
              alignItems: 'center',
              bgcolor: 'brand.transparent',
              borderRadius: 1,
              display: 'flex',
              height: 48,
              justifyContent: 'center',
              minWidth: 48
            }}
          >
            <Icon {...icon} />
          </Box>
        ) : null}

        <Box sx={{ flex: 1, position: 'relative' }}>
          <Box sx={{ maxWidth: 'title', position: 'relative', width: '100%' }}>
            <Box
              sx={{
                alignItems: { xs: 'flex-start', md: 'center' },
                display: 'flex',
                flexDirection: { xs: 'column-reverse', md: 'row' },
                gap: 2,
                mb: body ? '0.5rem' : undefined,
                width: '100%'
              }}
            >
              {editable ? (
                <EditableTitle
                  actions={true}
                  autoFocus={!editable.title}
                  onSubmit={editable.onSubmit}
                  placeholder={editable.placeholder}
                  sx={{ width: '100%' }}
                  value={editable.title}
                  variant={size === 'medium' ? 'h6' : 'h2'}
                />
              ) : (
                <Text
                  color="text.primary"
                  component="h1"
                  variant={size === 'medium' ? 'h6' : 'h2'}
                >
                  {title}
                </Text>
              )}

              {chip ? (
                <Chip {...chip} size={size === 'medium' ? 'small' : 'medium'} />
              ) : null}
            </Box>
            {body ? (
              <Text color="text.primary" component="div" variant="body1">
                {body}
              </Text>
            ) : null}
          </Box>
        </Box>

        {actions ? (
          <Box sx={{ display: 'flex', gap: 1, height: 'fit-content' }}>
            {actions}
          </Box>
        ) : null}
      </Box>

      {metaItems ? <MetaRow items={metaItems} sx={{ mt: 3 }} /> : null}
    </Box>
  )
})

Hero.displayName = 'Hero'
export default Hero
